import { useEffect, useRef, useState } from "react";
import type React from "react";
import { theme, card, btnPrimary, btnSecondary } from "../theme";
import { api, apiBlob, apiErrorMessage } from "../api/client";
import type { DocumentTemplateInfo } from "../types/executiveReport";

// My Organisation → Document template.
//
// The client uploads the Word document their own policies are written in. From
// then on every generated policy and executive report is rendered into it — the
// template's fonts, heading styles, colours, header/footer, cover page and page
// setup are used as they are. Uploads go straight to Blob Storage on a
// short-lived SAS and are confirmed (validated and sanitised) by the API.

const MAX_BYTES = 15 * 1024 * 1024;

const PLACEHOLDER_HELP: Record<string, string> = {
  title: "Document title",
  body: "Where the generated content goes (keeps your cover page)",
  document_control: "The version / owner / classification table",
  organisation: "Your organisation's name",
  version: "Version number",
  effective_date: "Effective date",
  next_review_date: "Next review date",
  owner: "Document owner",
  classification: "Classification (Internal, Confidential…)",
  date: "Generation date",
  document_type: "Policy, Privacy notice, Executive report…",
};

function saveBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

function errorMessage(e: unknown, fallback: string): string {
  return apiErrorMessage(e, fallback);
}

const swatch = (hex: string): React.CSSProperties => ({
  width: 18, height: 18, borderRadius: 3, background: `#${hex}`, border: `1px solid ${theme.color.border}`, flexShrink: 0,
});

export function DocumentTemplatePanel({ readOnly = false }: { readOnly?: boolean }) {
  const [template, setTemplate] = useState<DocumentTemplateInfo | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState<"upload" | "preview" | "example" | "remove" | "download" | null>(null);
  const [error, setError] = useState("");
  const [warnings, setWarnings] = useState<string[]>([]);
  const [notice, setNotice] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    api.get<{ template: DocumentTemplateInfo | null }>("/organizations/document-template")
      .then((d) => setTemplate(d.template))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  async function handleFile(file: File) {
    setError(""); setWarnings([]); setNotice("");
    const ext = /\.(docx|dotx)$/i.exec(file.name)?.[1]?.toLowerCase();
    if (!ext) { setError("Upload a Word document (.docx) or Word template (.dotx). Macro-enabled files (.docm / .dotm) are not accepted."); return; }
    if (file.size > MAX_BYTES) { setError("The template must be under 15 MB."); return; }
    const contentType = ext === "dotx"
      ? "application/vnd.openxmlformats-officedocument.wordprocessingml.template"
      : "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    setBusy("upload");
    try {
      const { uploadUrl, blobPath } = await api.post<{ uploadUrl: string; blobPath: string }>("/organizations/document-template/request-upload", {
        fileName: file.name, contentType, sizeBytes: file.size,
      });
      const put = await fetch(uploadUrl, { method: "PUT", body: file, headers: { "x-ms-blob-type": "BlockBlob", "Content-Type": contentType } });
      if (!put.ok) throw new Error("upload failed");
      const result = await api.post<{ template: DocumentTemplateInfo; warnings: string[] }>("/organizations/document-template/confirm", { blobPath, fileName: file.name });
      setTemplate(result.template);
      setWarnings(result.warnings);
      setNotice("Template uploaded. New policies and reports will use it from now on.");
    } catch (e) {
      setError(errorMessage(e, "Upload failed. Please try again."));
    } finally {
      setBusy(null);
    }
  }

  async function toggle(field: "applyToPolicies" | "applyToReports", value: boolean) {
    if (!template) return;
    const previous = template;
    setTemplate({ ...template, [field]: value });
    try {
      const r = await api.patch<{ template: DocumentTemplateInfo }>("/organizations/document-template", { [field]: value });
      setTemplate(r.template);
    } catch {
      setTemplate(previous);
      setError("Could not save the setting. Please try again.");
    }
  }

  async function remove() {
    if (!template || !window.confirm("Remove the template? Generated documents will go back to the platform's standard look.")) return;
    setBusy("remove"); setError("");
    try {
      await api.del("/organizations/document-template");
      setTemplate(null); setWarnings([]); setNotice("Template removed.");
    } catch {
      setError("Could not remove the template.");
    } finally {
      setBusy(null);
    }
  }

  async function preview() {
    setBusy("preview"); setError("");
    try {
      saveBlob(await apiBlob("/organizations/document-template/preview"), "Template-preview-Information-Security-Policy.docx");
    } catch {
      setError("The preview could not be generated. Check the template opens in Word and try again.");
    } finally {
      setBusy(null);
    }
  }

  async function example() {
    setBusy("example"); setError("");
    try {
      saveBlob(await apiBlob("/organizations/document-template/example"), "Abacas-document-template-example.docx");
    } catch {
      setError("The example template could not be downloaded.");
    } finally {
      setBusy(null);
    }
  }

  async function downloadStored() {
    setBusy("download"); setError("");
    try {
      const { url } = await api.get<{ url: string }>("/organizations/document-template/download-url");
      const a = document.createElement("a");
      a.href = url; a.download = ""; a.click();
    } catch {
      setError("The template could not be downloaded.");
    } finally {
      setBusy(null);
    }
  }

  const t = template?.theme;
  const pageLabel = t ? `${Math.abs(t.page.widthPt - 595) < 3 && Math.abs(t.page.heightPt - 842) < 3 ? "A4" : Math.abs(t.page.widthPt - 612) < 3 && Math.abs(t.page.heightPt - 792) < 3 ? "Letter" : `${Math.round(t.page.widthPt / 72 * 25.4)} × ${Math.round(t.page.heightPt / 72 * 25.4)} mm`} ${t.page.orientation}` : "";

  return (
    <div style={{ ...card, marginBottom: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 2 }}>Document template</div>
      <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 16, lineHeight: 1.5 }}>
        Upload the Word document your own policies are written in. Generated policies and executive reports are then produced in it —
        your fonts, heading styles, colours, header and footer, cover page and page setup are used exactly as they are.
        Add <code style={{ fontSize: 11 }}>{"{{body}}"}</code> where the generated content should go to keep your cover page;
        without it, only your styles, header, footer and page setup are used.
      </div>

      {!loaded ? (
        <div style={{ fontSize: 12, color: theme.color.faint }}>Loading…</div>
      ) : template ? (
        <>
          <div style={{ display: "flex", gap: 14, alignItems: "flex-start", flexWrap: "wrap", marginBottom: 14 }}>
            <div style={{ width: 52, height: 64, borderRadius: 3, border: `1px solid ${theme.color.border}`, background: theme.color.panelAlt, display: "flex", flexDirection: "column", overflow: "hidden", flexShrink: 0 }}>
              <div style={{ height: 8, background: t ? `#${t.colors.heading1}` : theme.color.accent }} />
              <div style={{ padding: "6px 6px 0", display: "flex", flexDirection: "column", gap: 3 }}>
                <div style={{ height: 4, width: "70%", background: t ? `#${t.colors.heading2}` : theme.color.accent, borderRadius: 2 }} />
                <div style={{ height: 3, width: "90%", background: theme.color.border, borderRadius: 2 }} />
                <div style={{ height: 3, width: "80%", background: theme.color.border, borderRadius: 2 }} />
                <div style={{ height: 3, width: "85%", background: theme.color.border, borderRadius: 2 }} />
              </div>
            </div>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading, wordBreak: "break-all" }}>{template.fileName}</div>
              <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 2 }}>
                Uploaded {new Date(template.uploadedAt).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })} · {Math.round(template.sizeBytes / 1024)} KB
              </div>
              <div style={{ marginTop: 8, display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 10px", borderRadius: 2, fontSize: 11, fontWeight: 600,
                background: template.mode === "placeholder" ? theme.color.successBg : theme.color.accentBg,
                color: template.mode === "placeholder" ? theme.color.successText : theme.color.accentText }}>
                {template.mode === "placeholder" ? "Your cover page is kept — {{body}} found" : "Style only — your styles, header and footer are applied"}
              </div>
            </div>
          </div>

          {t && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "10px 16px", marginBottom: 14, fontSize: 12 }}>
              <div>
                <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.04em", color: theme.color.faint, marginBottom: 3 }}>Fonts</div>
                <div style={{ color: theme.color.text }}>Headings: <strong>{t.fonts.heading}</strong></div>
                <div style={{ color: theme.color.text }}>Body: <strong>{t.fonts.body}</strong> {t.sizes.body}pt</div>
              </div>
              <div>
                <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.04em", color: theme.color.faint, marginBottom: 3 }}>Colours</div>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <span title={`Heading 1 #${t.colors.heading1}`} style={swatch(t.colors.heading1)} />
                  <span title={`Heading 2 #${t.colors.heading2}`} style={swatch(t.colors.heading2)} />
                  <span title={`Heading 3 #${t.colors.heading3}`} style={swatch(t.colors.heading3)} />
                  <span title={`Text #${t.colors.text}`} style={swatch(t.colors.text)} />
                  <span title={`Accent #${t.colors.accent[0]}`} style={swatch(t.colors.accent[0])} />
                </div>
              </div>
              <div>
                <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.04em", color: theme.color.faint, marginBottom: 3 }}>Page</div>
                <div style={{ color: theme.color.text }}>{pageLabel}</div>
                <div style={{ color: theme.color.subtle }}>{t.header.hasImage ? "Header logo found" : "No header image"}{t.footer.lines.length ? " · footer text kept" : ""}</div>
              </div>
            </div>
          )}

          {template.placeholders.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.04em", color: theme.color.faint, marginBottom: 5 }}>Placeholders found</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {template.placeholders.map((p) => (
                  <span key={p} title={PLACEHOLDER_HELP[p]} style={{ fontFamily: "monospace", fontSize: 11, padding: "2px 8px", borderRadius: 3, background: theme.color.panelAlt, border: `1px solid ${theme.color.border}`, color: theme.color.text }}>
                    {`{{${p}}}`}
                  </span>
                ))}
              </div>
            </div>
          )}

          {!readOnly && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", userSelect: "none", fontSize: 13, color: theme.color.text }}>
                <input type="checkbox" checked={template.applyToPolicies} onChange={(e) => toggle("applyToPolicies", e.target.checked)} style={{ width: 16, height: 16, cursor: "pointer" }} />
                Use for generated policy documents
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", userSelect: "none", fontSize: 13, color: theme.color.text }}>
                <input type="checkbox" checked={template.applyToReports} onChange={(e) => toggle("applyToReports", e.target.checked)} style={{ width: 16, height: 16, cursor: "pointer" }} />
                Use for executive reports (Word and PDF)
              </label>
            </div>
          )}

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button onClick={preview} disabled={busy !== null} style={{ ...btnPrimary, opacity: busy ? 0.6 : 1 }}>
              {busy === "preview" ? "Generating…" : "Preview a sample policy"}
            </button>
            {!readOnly && (
              <button onClick={() => inputRef.current?.click()} disabled={busy !== null} style={btnSecondary}>
                {busy === "upload" ? "Uploading…" : "Replace template"}
              </button>
            )}
            <button onClick={downloadStored} disabled={busy !== null} style={btnSecondary}>Download template</button>
            {!readOnly && (
              <button onClick={remove} disabled={busy !== null} style={{ ...btnSecondary, color: theme.color.dangerText }}>Remove</button>
            )}
          </div>
        </>
      ) : (
        <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
          {!readOnly && (
            <div
              onClick={() => !busy && inputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f && !busy) handleFile(f); }}
              style={{
                flex: 1, minWidth: 260, padding: "22px 18px", borderRadius: theme.radius.md, border: `2px dashed ${theme.color.borderStrong}`,
                background: theme.color.panelAlt, textAlign: "center", cursor: busy ? "default" : "pointer", color: theme.color.subtle, fontSize: 13, lineHeight: 1.5,
              }}
            >
              {busy === "upload" ? "Uploading and checking the template…" : <>Click or drop your Word template here<br /><span style={{ fontSize: 11, color: theme.color.faint }}>.docx or .dotx, up to 15 MB</span></>}
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 200 }}>
            <button onClick={example} disabled={busy !== null} style={btnSecondary}>
              {busy === "example" ? "Preparing…" : "Download an example template"}
            </button>
            <div style={{ fontSize: 11, color: theme.color.faint, lineHeight: 1.5 }}>
              Start from the example, restyle it in Word (fonts, colours, header logo, cover page) and upload it back. Macros, embedded objects and external links are removed on upload.
            </div>
          </div>
        </div>
      )}

      {!readOnly && (
        <input ref={inputRef} type="file" accept=".docx,.dotx" style={{ display: "none" }}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ""; }} />
      )}

      {notice && <div style={{ marginTop: 12, fontSize: 12, color: theme.color.successText }}>{notice}</div>}
      {warnings.length > 0 && (
        <div style={{ marginTop: 10, padding: "8px 12px", borderRadius: 3, background: theme.color.warningBg, border: `1px solid ${theme.color.warning}`, fontSize: 12, color: theme.color.warningText }}>
          {warnings.map((w) => <div key={w}>{w}</div>)}
        </div>
      )}
      {error && <div style={{ marginTop: 10, fontSize: 12, color: theme.color.dangerText }}>{error}</div>}
    </div>
  );
}
